/**
 * Permission matrix config — groups the 194 permission slugs from the Laravel
 * `permissions` table into logical sections so the role-form UI can render a
 * grouped checkbox grid with select-all per group.
 */

export interface PermissionGroup {
  /** i18n key OR display label */
  label: string;
  permissions: string[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    label: 'User Management',
    permissions: ['users_view', 'users_add', 'users_edit', 'users_delete', 'record_view'],
  },
  {
    label: 'Group Permissions',
    permissions: ['permissions_view', 'permissions_add', 'permissions_edit', 'permissions_delete'],
  },
  {
    label: 'Products',
    permissions: [
      'products_view',
      'products_add',
      'products_edit',
      'products_delete',
      'product_import',
      'opening_stock_import',
      'count_stock',
      'barcode_view',
      'category',
      'subcategory',
      'brand',
      'unit',
    ],
  },
  {
    label: 'Customers',
    permissions: ['Customers_view', 'Customers_add', 'Customers_edit', 'Customers_delete', 'customers_import', 'pay_due'],
  },
  {
    label: 'Suppliers',
    permissions: ['Suppliers_view', 'Suppliers_add', 'Suppliers_edit', 'Suppliers_delete', 'Suppliers_import', 'pay_supplier_due'],
  },
  {
    label: 'Sales',
    permissions: [
      'Sales_view',
      'Sales_add',
      'Sales_edit',
      'Sales_delete',
      'edit_product_sale',
      'edit_tax_discount_shipping_sale',
      'Sale_Returns_view',
      'Sale_Returns_add',
      'Sale_Returns_edit',
      'Sale_Returns_delete',
      'pay_sale_return_due',
      'payment_sales_view',
      'payment_sales_add',
      'payment_sales_edit',
      'payment_sales_delete',
      'payment_returns_view',
      'payment_returns_add',
      'payment_returns_edit',
      'payment_returns_delete',
      'shipment',
      'Pos_view',
      'pos_settings',
    ],
  },
  {
    label: 'Quotations',
    permissions: [
      'Quotations_view',
      'Quotations_add',
      'Quotations_edit',
      'Quotations_delete',
      'edit_product_quotation',
      'edit_tax_discount_shipping_quotation',
    ],
  },
  {
    label: 'Purchases',
    permissions: [
      'Purchases_view',
      'Purchases_add',
      'Purchases_edit',
      'Purchases_delete',
      'edit_product_purchase',
      'edit_tax_discount_shipping_purchase',
      'Purchase_Returns_view',
      'Purchase_Returns_add',
      'Purchase_Returns_edit',
      'Purchase_Returns_delete',
      'pay_purchase_return_due',
      'payment_purchases_view',
      'payment_purchases_add',
      'payment_purchases_edit',
      'payment_purchases_delete',
    ],
  },
  {
    label: 'Inventory',
    permissions: [
      'transfer_view',
      'transfer_add',
      'transfer_edit',
      'transfer_delete',
      'adjustment_view',
      'adjustment_add',
      'adjustment_edit',
      'adjustment_delete',
      'damage_view',
      'warehouse',
    ],
  },
  {
    label: 'Accounting',
    permissions: [
      'account',
      'transfer_money',
      'expense_view',
      'expense_add',
      'expense_edit',
      'expense_delete',
      'deposit_view',
      'deposit_add',
      'deposit_edit',
      'deposit_delete',
      'accounting_dashboard',
      'chart_of_accounts',
      'journal_entries',
      'trial_balance',
      'balance_sheet',
      'accounting_profit_loss',
      'accounting_tax_report',
    ],
  },
  {
    label: 'HR',
    permissions: [
      'view_employee',
      'add_employee',
      'edit_employee',
      'delete_employee',
      'attendance',
      'leave',
      'payroll',
      'holiday',
      'office_shift',
      'department',
      'designation',
      'company',
    ],
  },
  {
    label: 'Service & Projects',
    permissions: [
      'service_jobs',
      'bookings',
      'projects',
      'tasks',
      'assets',
      'subscription_product',
      'customer_maintenance_history_report',
    ],
  },
  {
    label: 'Online Store',
    permissions: [
      'Banners_view',
      'Collections_view',
      'Messages_view',
      'Orders_view',
      'Subscribers_view',
      'Store_settings_view',
      'notification_template',
    ],
  },
  {
    label: 'Reports',
    permissions: [
      'dashboard',
      'Reports_sales',
      'Reports_purchase',
      'Reports_profit',
      'Reports_customers',
      'Reports_suppliers',
      'Reports_payments_Sales',
      'Reports_payments_Purchases',
      'Reports_payments_Sale_Returns',
      'Reports_payments_purchase_Return',
      'Reports_quantity_alerts',
      'Top_products',
      'Top_customers',
      'Top_Suppliers_Report',
      'Stock_Aging_Report',
      'Stock_Adjustment_Report',
      'Stock_Inventory_Valuation',
      'Stock_Transfer_Report',
      'Dead_Stock_Report',
      'Warehouse_report',
      'users_report',
      'cash_flow_report',
      'cash_register_report',
      'discount_summary_report',
      'expenses_report',
      'deposits_report',
      'inactive_customers_report',
      'analytics_report',
      'AI_Reports',
      'customer_loyalty_points_report',
      'tax_summary_report',
      'seller_report',
      'inventory_valuation',
      'negative_stock_report',
      'draft_invoices_report',
      'zeroSalesProducts',
      'product_purchases_report',
      'product_sales_report',
      'product_report',
      'service_jobs_report',
      'checklist_completion_report',
      'return_ratio_report',
      'report_attendance_summary',
      'report_device_management',
      'report_error_logs',
      'report_sales_by_brand',
      'report_sales_by_category',
      'report_transactions',
      'report_warranty',
      'stock_report',
    ],
  },
  {
    label: 'Settings',
    permissions: [
      'setting_system',
      'mail_settings',
      'sms_settings',
      'payment_gateway',
      'payment_methods',
      'appearance_settings',
      'update_settings',
      'translations_settings',
      'module_settings',
      'currency',
      'backup',
      'login_device_management',
    ],
  },
  {
    label: 'Integrations',
    permissions: ['quickbooks_settings', 'woocommerce_settings', 'customer_display_screen_setup'],
  },
];

/** All known permission slugs (deduplicated). */
export const ALL_PERMISSIONS: string[] = Array.from(
  new Set(PERMISSION_GROUPS.flatMap((g) => g.permissions)),
);
